/**
 * Dashboard — phase 7 Track A: the four-destination IA from the Claude
 * Design handoff (Tonight / Swarm / Schedules / Activity), ONE responsive
 * app (phone tab bar <900px, desktop sidebar ≥900px — CSS decides, see
 * dashboard.css). Every datum binds to the live plane via api.ts; the
 * phase-6 tenant switcher, review digest, and run modes ride along.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "../dashboard.css";
import { useAuth } from "../hooks/useAuth";
import { useTenant } from "../lib/tenant";
import {
  getAgents,
  getJobs,
  getMachines,
  getMachineSessions,
  getSchedules,
  getSessionEvents,
  updateSchedule,
  type Agent,
  type Job,
  type Machine,
  type Schedule,
  type Session,
  type SessionEvent,
} from "../lib/api";
import { Icon, I } from "../kit";
import { DashShell, type DashTab } from "../components/DashShell";
import { TonightTab } from "../components/TonightTab";
import { SwarmTab } from "../components/SwarmTab";
import { SchedulesTab } from "../components/SchedulesTab";
import { ActivityTab } from "../components/ActivityTab";
import { AgentSheet } from "../components/AgentSheet";
import { ScheduleEditor } from "../components/ScheduleEditor";

const POLL_MS = 10_000;
const MAX_JOBS = 30;
const MAX_SESSIONS = 30;
const TICKER_LOOKBACK = 15;

const TAB_KEYS: DashTab[] = ["tonight", "swarm", "schedules", "activity"];

type SheetState =
  | { type: "agent"; agent: Agent | null }
  | { type: "sched"; schedule: Schedule | null }
  | null;

/** Newest tool/assistant activity from a live session's transcript tail —
 *  the "live tool ticker" on the working-now card. */
function lastActivity(events: SessionEvent[]): string | null {
  for (let i = events.length - 1; i >= 0; i--) {
    const ev = events[i];
    if (ev.kind !== "transcript") continue;
    let parsed: { type?: string; message?: { content?: unknown } };
    try {
      parsed = JSON.parse(ev.payload) as typeof parsed;
    } catch {
      continue; // partial/corrupt line — skip
    }
    if (parsed?.type !== "assistant") continue;
    const content = parsed.message?.content;
    if (typeof content === "string" && content.trim()) {
      return content.replace(/\s+/g, " ").trim().slice(0, 90);
    }
    if (Array.isArray(content)) {
      const items = content as { type?: string; text?: string; name?: string }[];
      const tool = items.find((c) => c?.type === "tool_use" && c.name);
      if (tool?.name) return `tool ${tool.name}`;
      const text = items.find(
        (c) => c?.type === "text" && typeof c.text === "string" && c.text.trim()
      );
      if (text?.text) return text.text.replace(/\s+/g, " ").trim().slice(0, 90);
    }
  }
  return null;
}

export default function Dashboard() {
  const { status, user, signOut } = useAuth();
  const navigate = useNavigate();
  const { tab: tabParam } = useParams<{ tab?: string }>();
  const tab: DashTab = TAB_KEYS.includes(tabParam as DashTab)
    ? (tabParam as DashTab)
    : "tonight";

  // Phase 6 §2: every api.ts call rides the selected tenant; depending on
  // tenantId here makes a switch refetch immediately instead of next poll.
  const { tenantId } = useTenant();

  const [machines, setMachines] = useState<Machine[] | null>(null);
  const [sessions, setSessions] = useState<Session[] | null>(null);
  const [agents, setAgents] = useState<Agent[] | null>(null);
  const [schedules, setSchedules] = useState<Schedule[] | null>(null);
  const [jobs, setJobs] = useState<Job[] | null>(null);
  const [ticker, setTicker] = useState<string | null>(null);

  const [err, setErr] = useState<string | null>(null);
  const [agentsErr, setAgentsErr] = useState<string | null>(null);
  const [schedErr, setSchedErr] = useState<string | null>(null);
  const [jobsErr, setJobsErr] = useState<string | null>(null);

  const [sheet, setSheet] = useState<SheetState>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const flash = useCallback((message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(null), 2400);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const ms = await getMachines();
      setMachines(ms);
      const merged = (
        await Promise.all(
          ms.map((m) => getMachineSessions(m.machineId).catch(() => []))
        )
      ).flat();
      const all = [...new Map(merged.map((s) => [s.sessionId, s])).values()];
      all.sort((a, b) => (b.startedAt || "").localeCompare(a.startedAt || ""));
      const top = all.slice(0, MAX_SESSIONS);
      setSessions(top);
      setErr(null);

      // Live tool ticker for the working-now card: read the transcript
      // tail of the most recent live session.
      const live = top.find(
        (s) => s.status === "running" || s.status === "starting"
      );
      if (live) {
        try {
          const since = Math.max(0, (live.lastSeq ?? 0) - TICKER_LOOKBACK);
          const ev = await getSessionEvents(live.sessionId, since);
          setTicker(lastActivity(ev.events));
        } catch {
          setTicker(null); // ticker is garnish — never block the card
        }
      } else {
        setTicker(null);
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : "failed to load");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- tenantId rides via api.ts; it's a refetch trigger
  }, [tenantId]);

  const refreshAgents = useCallback(async () => {
    try {
      setAgents(await getAgents());
      setAgentsErr(null);
    } catch (e) {
      setAgentsErr(e instanceof Error ? e.message : "failed to load");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- tenantId rides via api.ts; it's a refetch trigger
  }, [tenantId]);

  const refreshSchedules = useCallback(async () => {
    try {
      setSchedules(await getSchedules());
      setSchedErr(null);
    } catch (e) {
      setSchedErr(e instanceof Error ? e.message : "failed to load");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- tenantId rides via api.ts; it's a refetch trigger
  }, [tenantId]);

  const refreshJobs = useCallback(async () => {
    try {
      setJobs(await getJobs({ limit: MAX_JOBS }));
      setJobsErr(null);
    } catch (e) {
      setJobsErr(e instanceof Error ? e.message : "failed to load");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- tenantId rides via api.ts; it's a refetch trigger
  }, [tenantId]);

  useEffect(() => {
    if (status === "signed-out") navigate("/", { replace: true });
  }, [status, navigate]);

  useEffect(() => {
    if (status !== "signed-in") return;
    // Tenant switch: drop the previous tenant's data so sections show
    // their loading states instead of another tenant's rows.
    setMachines(null);
    setSessions(null);
    setAgents(null);
    setSchedules(null);
    setJobs(null);
    setTicker(null);
    const tick = () => {
      refresh();
      refreshAgents();
      refreshSchedules();
      refreshJobs();
    };
    tick();
    const id = setInterval(tick, POLL_MS);
    return () => clearInterval(id);
  }, [status, refresh, refreshAgents, refreshSchedules, refreshJobs]);

  const machineName = useCallback(
    (id: string) =>
      machines?.find((m) => m.machineId === id)?.name ?? id.slice(0, 8),
    [machines]
  );

  const toggleSchedule = async (s: Schedule) => {
    setTogglingId(s.scheduleId);
    try {
      await updateSchedule(s.scheduleId, { enabled: !s.enabled });
      await refreshSchedules();
    } catch (e) {
      setSchedErr(e instanceof Error ? e.message : "update failed");
    } finally {
      setTogglingId(null);
    }
  };

  if (status === "loading") {
    return (
      <div className="dsh-app">
        <p className="hint" style={{ padding: 48 }}>
          Loading…
        </p>
      </div>
    );
  }

  // First-run teaching mode: an empty swarm (per the prototype's
  // empty-state flow) — loaded, and nothing enrolled or stood up yet.
  const firstRun =
    machines !== null &&
    machines.length === 0 &&
    agents !== null &&
    agents.length === 0;

  const working =
    sessions?.find(
      (s) => s.status === "running" || s.status === "starting"
    ) ?? null;

  const openNewSchedule = () => setSheet({ type: "sched", schedule: null });
  const openNewAgent = () => setSheet({ type: "agent", agent: null });

  let screen;
  if (tab === "tonight") {
    screen = (
      <TonightTab
        userEmail={user?.email}
        machines={machines}
        sessions={sessions}
        schedules={schedules}
        jobs={jobs}
        working={working}
        ticker={ticker}
        firstRun={firstRun}
        machineName={machineName}
        onNewAgent={openNewAgent}
        onNewSchedule={openNewSchedule}
      />
    );
  } else if (tab === "swarm") {
    screen = (
      <SwarmTab
        machines={machines}
        agents={agents}
        tenantId={tenantId}
        err={err}
        agentsErr={agentsErr}
        machineName={machineName}
        onOpenAgent={(a) => setSheet({ type: "agent", agent: a })}
        onNewAgent={openNewAgent}
        onMachinesChanged={refresh}
      />
    );
  } else if (tab === "schedules") {
    screen = (
      <SchedulesTab
        schedules={schedules}
        err={schedErr}
        togglingId={togglingId}
        onToggle={toggleSchedule}
        onEdit={(s) => setSheet({ type: "sched", schedule: s })}
        onNew={openNewSchedule}
      />
    );
  } else {
    screen = (
      <ActivityTab
        jobs={jobs}
        sessions={sessions}
        agents={agents}
        jobsErr={jobsErr}
        sessionsErr={err}
        machineName={machineName}
        onJobsChanged={refreshJobs}
        notify={flash}
      />
    );
  }

  return (
    <>
      <DashShell
        active={tab}
        crumb={tab}
        userEmail={user?.email}
        entitlements={user?.ownedEntitlements}
        onSignOut={signOut}
        onNewSchedule={openNewSchedule}
      >
        {err && tab === "tonight" && (
          <p className="hint" style={{ marginBottom: 16 }}>
            Couldn't reach the plane ({err}) — retrying…
          </p>
        )}
        {screen}
      </DashShell>

      {sheet?.type === "sched" && (
        <ScheduleEditor
          seed={sheet.schedule}
          agents={agents ?? []}
          onClose={() => setSheet(null)}
          onSaved={(note) => {
            setSheet(null);
            flash(note);
            refreshSchedules();
          }}
        />
      )}
      {sheet?.type === "agent" && (
        <AgentSheet
          agent={sheet.agent}
          machines={machines ?? []}
          onClose={() => setSheet(null)}
          onChanged={() => {
            refreshAgents();
            refresh();
          }}
        />
      )}

      {toast && (
        <div className="dsh-toast" role="status">
          <div className="dsh-toast-inner">
            <Icon d={I.check} size={14} />
            {toast}
          </div>
        </div>
      )}
    </>
  );
}
